import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import Typist from "react-typist";

import {
  Box,
  Icon,
  Inline,
  Text,
  TextInput,
  useBreakpoint,
  useTheme
} from "@seneca/senekit";

import { queryClient } from "services";
import { renameSession } from "services/chatbotService/client";
import { getSessionsQueryOptions } from "services/chatbotService/queries";

import { KeyboardShortcut } from "seneca-common/utils/components/keyboard-shortcuts";
import { validateCharacterLimit } from "seneca-common/utils/form-hook/utils/validationFunctions";
import useHover from "seneca-common/utils/hooks/useHover";
import { useTypedState } from "seneca-common/utils/routing/react-router-typesafe-routes/dom";

import {
  logUserRenamedChat,
  logUserSelectedPastChat
} from "features/classroom/common/Amelia/analytics";
import { ROUTES } from "routing/routes";

export default function SessionSidebarItem({
  title,
  sessionId,
  courseId,
  isActive
}: {
  title: string;
  isActive: boolean;
  courseId: string;
  sessionId: string;
}) {
  const { isNewSession } = useTypedState(
    ROUTES.CLASSROOM.COURSE.ASK_AMELIA.CHAT
  );
  const navigate = useNavigate();
  const themes = useTheme();

  const [text, setText] = useState(title);
  const [isValidText, setIsValidText] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const [rowHoverRef, isOnRowHover] = useHover();
  const [deleteIconHoverRef, isOnDeleteIconHover] = useHover();
  const [editIconHoverRef, isOnEditIconHover] = useHover();
  const [saveIconHoverRef, isOnSaveIconHover] = useHover();
  const { isDesktopOnly } = useBreakpoint();

  const { mutateAsync: rename, isPending } = useMutation({
    mutationFn: renameSession,
    onSuccess() {
      queryClient.invalidateQueries({ ...getSessionsQueryOptions() });
      logUserRenamedChat({ chatbotSessionId: sessionId });
    }
  });

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = async (sessionId: string) => {
    if (!isValidText) return;
    setIsEditing(false);
    await rename({ sessionId, title: text });
  };

  function handleEnterKeyPress(event: KeyboardEvent) {
    event.preventDefault();
    handleSaveClick(sessionId);
  }

  function handleDeleteClick() {
    navigate(
      ROUTES.CLASSROOM.COURSE.ASK_AMELIA.CHAT.DELETE_MODAL.buildPath({
        sessionId,
        courseId
      })
    );
  }

  function validateText(text: string) {
    const isValid = validateCharacterLimit(1, 64)(text) === true;
    setIsValidText(isValid);
  }

  const isNewChat = isNewSession && isActive;

  return (
    <Box
      padding="12"
      background={isActive ? themes["primary-low-surface"] : "none"}
      hoverStyles={{
        backgroundColor: themes["neutral-low-surface"]
      }}
      borderRadius="none"
      border="none"
      component="button"
      display="flex"
      alignItems="center"
      flexWrap="nowrap"
      cursor="pointer"
      width="100%"
      onClick={() => {
        if (isActive) return;
        logUserSelectedPastChat({ chatbotSessionId: sessionId });
        navigate(
          ROUTES.CLASSROOM.COURSE.ASK_AMELIA.CHAT.buildPath({
            courseId,
            sessionId: sessionId
          })
        );
      }}
      justifyContent="flex-start"
      ref={rowHoverRef as any}
    >
      <Box width="100%" paddingRight="12">
        {isEditing ? (
          <KeyboardShortcut shortcut="enter" handler={handleEnterKeyPress}>
            <TextInput
              id={sessionId}
              type="text"
              value={text}
              onChange={e => {
                setText(e.target.value);
                validateText(e.target.value);
              }}
              disabled={isPending}
              error={!isValidText}
            />
          </KeyboardShortcut>
        ) : (
          <Box maxWidth={isOnRowHover && !isEditing ? "200px" : "none"}>
            <Text truncate align="left" dataNoTranslate>
              {isNewChat ? (
                <Typist avgTypingDelay={30} cursor={{ show: false }}>
                  {text}
                </Typist>
              ) : (
                text
              )}
            </Text>
          </Box>
        )}
      </Box>
      {isDesktopOnly && (
        <Inline flexWrap="nowrap" space="large">
          {!isEditing && isOnRowHover && (
            <Box
              onClick={e => {
                e.stopPropagation();
                handleDeleteClick();
              }}
              ref={deleteIconHoverRef as any}
            >
              <Icon
                size="medium"
                accessibilityLabel="Delete chat"
                iconName="trash"
                tone={isOnDeleteIconHover ? "primary" : "neutral"}
              />
            </Box>
          )}
          {isEditing ? (
            <Box
              onClick={e => {
                e.stopPropagation();
                handleSaveClick(sessionId);
              }}
              disabled={!isValidText}
              ref={saveIconHoverRef as any}
            >
              <Icon
                size="medium"
                accessibilityLabel="Save chat name"
                iconName="tick"
                tone={isOnSaveIconHover ? "success" : "neutral"}
              />
            </Box>
          ) : (
            isOnRowHover && (
              <Box
                onClick={e => {
                  e.stopPropagation();
                  handleEditClick();
                }}
                ref={editIconHoverRef as any}
              >
                <Icon
                  size="medium"
                  accessibilityLabel="Rename chat"
                  iconName="edit"
                  tone={isOnEditIconHover ? "primary" : "neutral"}
                />
              </Box>
            )
          )}
        </Inline>
      )}
    </Box>
  );
}
